import React from 'react'

import { Form } from 'antd'
import moment from 'moment'

import AddressAutocomplete from '@/components/form-components/AddressAutocomplete'
import { MobileInput } from '@/components/form-components/MobileInput'
import { DatePicker } from '@/components/shared-components/BUI/DatePicker'
import { Input } from '@/components/shared-components/BUI/Input'
import { Select, Option } from '@/components/shared-components/BUI/Select'
import { Language, useLocale } from '@/context/localeContext'
import {
  TRADES,
  TRADE_EMOJIS,
  EMPLOYMENT_STATUSES,
} from '@/modules/workers/constants'
import { filterSelect } from '@/utils/index'

import {
  BuilderSubbiesForSearchUiQuery,
  WorkerUiQuery,
} from 'types/api.graphql'

export const rules = {
  firstName: [{ required: true, message: 'Please enter their first name' }],
  lastName: [{ required: true, message: 'Please enter their last name' }],
  subcontractor: [{ required: true, message: 'Please select a subcontractor' }],
  mobile: [{ pattern: /\d{9}/, message: 'Mobile must be 9 digits.' }],
}

type Props = {
  subbies?: BuilderSubbiesForSearchUiQuery['subcontractorsByBuilder']
}

export const getFormValuesFromWorker = (
  worker: WorkerUiQuery['worker'],
  tLanguage: Language
) => {
  if (!worker) return {}

  const selectedTrade =
    !worker.trade || TRADES[tLanguage].includes(worker.trade)

  return {
    firstName: worker.user.firstName,
    lastName: worker.user.lastName,
    email: worker.user.email,
    trade: selectedTrade ? worker.trade : 'Other',
    ...(!selectedTrade && { tradeOther: worker.trade }),
    address: worker.address,
    mobile: worker.mobile,
    dob: worker.dob ? moment(worker.dob) : undefined,
    employmentStatus: worker.employmentStatus,
    emergencyContact: worker.emergencyContact,
    emergencyContactNumber: worker.emergencyContactNumber,
    emergencyContactRelationship: worker.emergencyContactRelationship,
    medicalInformation: worker.medicalInformation,
    allergies: worker.allergies,
  }
}

export const getVariablesFromForm = (values: Record<string, string>) => {
  let trade = values.trade
  if (values.trade === 'Other') {
    trade = values.tradeOther
  }

  return {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    trade,
    address: values.address,
    mobile: values.mobile,
    dob: values.dob,
    employmentStatus: values.employmentStatus,
    emergencyContact: values.emergencyContact,
    emergencyContactNumber: values.emergencyContactNumber,
    emergencyContactRelationship: values.emergencyContactRelationship,
    medicalInformation: values.medicalInformation,
    allergies: values.allergies,
    ...(values.subcontractorId && { subcontractorId: values.subcontractorId }),
  }
}

export const WorkerForm: React.FC<Props> = ({ subbies }) => {
  const { t, tLanguage } = useLocale()
  return (
    <>
      {!!subbies && (
        <Form.Item
          name="subcontractorId"
          label="Company"
          rules={rules.subcontractor}
        >
          <Select
            showSearch
            placeholder="Select a company"
            optionFilterProp="children"
            filterOption={filterSelect}
          >
            {subbies.map((subbie) => (
              <Option key={subbie.id} value={subbie.id}>
                {subbie.company.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item name="firstName" label="First name" rules={rules.firstName}>
        <Input placeholder="John" />
      </Form.Item>

      <Form.Item name="lastName" label="Last name" rules={rules.lastName}>
        <Input placeholder="Smith" />
      </Form.Item>

      <Form.Item name="email" label="Email">
        <Input placeholder="johnsmith@example.com" />
      </Form.Item>

      <Form.Item name="trade" label="Trade">
        <Select
          showSearch
          placeholder="Select a trade"
          optionFilterProp="label"
          options={TRADES[tLanguage].map((trade) => ({
            value: trade,
            label: `${trade} ${TRADE_EMOJIS[trade] ?? ''}`,
          }))}
        />
      </Form.Item>

      <Form.Item dependencies={['trade']} noStyle>
        {({ getFieldValue }) => {
          const trade = getFieldValue('trade') as string
          const customTrade =
            !!trade?.length && !TRADES[tLanguage].includes(trade)

          if (customTrade || trade === 'Other') {
            return (
              <Form.Item name="tradeOther" style={{ marginTop: '-1rem' }}>
                <Input placeholder="What is your trade?" />
              </Form.Item>
            )
          }
        }}
      </Form.Item>

      <Form.Item name="address" label="Full address">
        <AddressAutocomplete
          useLabelAsValue
          placeholder={t('worker:placeholders.addressAutocomplete')}
        />
      </Form.Item>

      <Form.Item name="mobile" label="Mobile number" rules={rules.mobile}>
        <MobileInput />
      </Form.Item>

      <Form.Item name="dob" label="Date of birth">
        <DatePicker defaultPickerValue={moment('1990-01-01')} format="L" />
      </Form.Item>

      <Form.Item name="employmentStatus" label="Employment status">
        <Select
          showSearch
          placeholder="Full time"
          optionFilterProp="children"
          filterOption={filterSelect}
        >
          {EMPLOYMENT_STATUSES[tLanguage].map((status, i) => (
            <Option key={i} value={status}>
              {status}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="emergencyContact" label="Emergency contact">
        <Input placeholder="Jane Smith" />
      </Form.Item>

      <Form.Item
        name="emergencyContactNumber"
        label="Emergency contact number"
        rules={rules.mobile}
      >
        <MobileInput />
      </Form.Item>

      <Form.Item
        name="emergencyContactRelationship"
        label="Emergency contact relationship"
      >
        <Input placeholder="Partner" />
      </Form.Item>

      <Form.Item name="medicalInformation" label="Medical Information">
        <Input placeholder="Takes heart medication" />
      </Form.Item>

      <Form.Item name="allergies" label="Allergies">
        <Input placeholder="Allergic to nuts" />
      </Form.Item>
    </>
  )
}
